import { ProbateDetails } from "../models/ProbateDetails";
import { callDelete, callGet, callPost } from "./index";
import { ProbateField, } from "../models/ProbateField";
import { AtticusForm } from "../models/AtticusForm";
import { LeadAPI } from "./LeadAPI";
import { knowledgeEngineDataPrefix } from "LeadProbateContextWrapper";

export class ProbateAPI {
  async getProbateDetails(
    estateId: number,
    userId: number,
    queryString?: string
  ): Promise<ProbateDetails | undefined> {
    try {
      let path = `estates/${estateId}/users/${userId}/determine-probate-type`;
      if (queryString) path += `?${queryString}`;
      const response = await callGet(path, true);
      if(!response?.data) {
        return undefined
      }
      return ProbateDetails.fromJSON(response?.data);
    } catch (error) {
      console.error(error, "getProbateDetails error");
    }
  }

  async getProbateDetailsForLead(
    countryId: number,
    stateId: number,
    givenAnswers: ProbateField[],
  ): Promise<ProbateDetails | undefined> {
    try {
      // we need to base64 encode these values because they
      // sometimes include non-ISO-8859-1 characters such as curvey single quotes
      const answersHeaderValue = JSON.stringify(givenAnswers || [])
      const path = `leads/determine-probate-type`;
      const response = await callPost(path,
        {
          given_answers: answersHeaderValue,
          country_id: countryId,
          state_id: stateId,
        },
        true);
        if(!response?.data) {
          return undefined
        }
      return ProbateDetails.fromJSON(response?.data);
    } catch (error) {
      if ((error as any)?.response?.status === 500) {
        console.error("got 500 from api")
        return undefined
      }
      throw error
    }
  }

  async getFieldAnswerForUser(userId:number,fieldId: string): Promise<string | boolean | number | undefined> {
    try {
      const path = `users/${userId}/knowledge-engine/field-answer/${fieldId}`;
      const response = await callGet(
        path,
        true
      );
      return response?.data?.answer_value
    } catch (error) {
      console.error(error, "getFieldAnswerForUser error");
    }

    return undefined
  }

  async saveFieldToKnowledgeEngine(
    userId:number,
    field: ProbateField,
    rethrowError = false
  ): Promise<any | undefined> {
    try {
      if (field?.id) {
        const path = `users/${userId}/knowledge-engine/field-answer/${field.id}`;
        return await callPost(
          path,
          {
            answer_value: field.answerValue,
          },
          rethrowError
        );
      } else {
        throw new Error("field id unknown");
      }
    } catch (error) {
      console.error(error, "saveFieldToKnowledgeEngine error");
    }
  }

  async saveFieldToKnowledgeEngineForLead(
    leadKey: string,
    field: ProbateField,
  ): Promise<any | undefined> {
    try {
      if (field?.id) {
        await LeadAPI.upsertLeadData(leadKey, { [knowledgeEngineDataPrefix + field.id]: field.answer })
      } else {
        throw new Error("field id unknown");
      }
    } catch (error) {
    }
  }

  /**
   * will delete knowledge engine answers for this estate
   * @param field
   * @param rethrowError
   */
  async deleteFieldsInKnowledgeEngine(
    estateId:number,
    field: ProbateField | string,
    rethrowError = false
  ): Promise<any | undefined> {
    try {
      if (field instanceof ProbateField) {
        // NOTE: this is currently not supported by the laravel api
        // if (field?.id) {
        //   const path = `/knowledge-engine/field-answer/${field.id}`;
        //   return await callPost(
        //     path,
        //     {
        //       answer_value: field.answerValue,
        //     },
        //     rethrowError
        //   );
        // } else {
        //   throw new Error("field id unknown");
        // }
      } else if (field === "all") {
        const path = `knowledge-engine/estates/${estateId}/delete-answers`;
        return await callDelete(path, rethrowError);
      } else {
        throw new Error("delete operation unsupported");
      }
    } catch (error) {
      console.error(error, "deleteFieldInKnowledgeEngine error");
    }
  }


  async deleteFieldsInKnowledgeEngineForLead(
    leadKey: string,
    field: ProbateField,
  ): Promise<any | undefined> {
    try {
      if (field?.id) {
        await LeadAPI.upsertLeadData(leadKey, { [knowledgeEngineDataPrefix + field.id]: null })
      } else {
        throw new Error("field id unknown");
      }
    } catch (error) {
    }
  }

  async getProbatePacket(estateId:number): Promise<AtticusForm[] | undefined> {
    try {
      const path = `estates/${estateId}/probate-packet`;
      const packetResponse = await callGet(path, true);
      return packetResponse?.data?.map((json: any) =>
        AtticusForm.fromJSON(json)
      );
    } catch (error) {
      // NOTE: intentionally not signing out when 401 is returned
      return undefined;
    }
  }
}
